import React from 'react';
import { Package } from 'lucide-react';
/* ⛔ `/view`, NOT the root barrel: the root re-exports building helpers that reach for a query client. */
import { EntityIcon } from '@hqo/entity-icon/view';

interface AddOnProductIconProps {
  imageUrl?: string;
  imageAltText?: string;
  /* Tailwind classes merged onto the tile, to fill a slot that isn't one of the package's sizes. */
  className?: string;
  testId?: string;
}

/* Decorative: the product name always sits beside it. */
const PRODUCT_FALLBACK = <Package aria-hidden="true" className="size-1/2 text-muted-foreground" />;

/**
 * VIS-2193. The product thumbnail on an add-on, the shared `EntityIcon` box that catalog-admin and the
 * vendor portal preview with: the catalog image, or the `Package` box when there is none or it fails
 * to load (EntityIcon swaps to the fallback on error). Without `imageUrl` it is just the box, which is
 * what the older add-on tile and modal use as their image fallback.
 *
 * The alt text is the catalog's, or EMPTY when the API sends none (old and third-party add-ons): the
 * product name is already visible next to the image, so a name-as-alt would be read twice. The box on
 * its own carries nothing to read, so it is hidden from assistive technology.
 *
 * Keyed on the url so a row that changes product starts over rather than keeping a failed-load state.
 */
export const AddOnProductIcon = ({
  imageUrl,
  imageAltText,
  className,
  testId = 'add-on-product-icon',
}: AddOnProductIconProps): JSX.Element => (
  <span className="shrink-0" data-testid={testId} aria-hidden={imageUrl ? undefined : 'true'}>
    <EntityIcon
      key={imageUrl ?? 'no-image'}
      imageUrl={imageUrl}
      alt={imageAltText ?? ''}
      size="md"
      shape="rounded"
      fallback={PRODUCT_FALLBACK}
      className={className}
    />
  </span>
);
